// IndexedDB on the phone: 10-second segments and recordings that are not yet confirmed by the server (ТЗ §8.5).
// Everything is keyed by user: another user on the same phone never sends or deletes them.

export interface LocalSegment {
  userId: string;
  visitId: string;
  recordingNo: number;
  seq: number;
  stream: number;
  blob: Blob;
  sha256: string;
  durationMs: number;
  recordedAt: string;
}

export interface LocalRecording {
  userId: string;
  visitId: string;
  recordingNo: number;
  mimeType: string;
  format: "webm" | "mp4" | "ogg";
  patientName?: string;
  startedAt: string;
  produced: number; // segments created on the device
  durationMs: number;
  finished?: { endedAt: string; endedBy: "doctor" | "autopause" | "limit"; segmentsTotal: number; durationMs: number };
  finishSent?: boolean;
  serverStatus?: string;
}

const DB_NAME = "aisol-med-recordings";
let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains("segments")) {
          const s = db.createObjectStore("segments", { keyPath: ["userId", "visitId", "recordingNo", "seq"] });
          s.createIndex("byRecording", ["userId", "visitId", "recordingNo"]);
          s.createIndex("byUser", "userId");
        }
        if (!db.objectStoreNames.contains("recordings")) {
          const r = db.createObjectStore("recordings", { keyPath: ["userId", "visitId", "recordingNo"] });
          r.createIndex("byUser", "userId");
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  return dbPromise;
}

function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  return open().then(
    (db) =>
      new Promise<T | undefined>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const r = fn(t.objectStore(store));
        let result: T | undefined;
        if (r) r.onsuccess = () => (result = r.result);
        t.oncomplete = () => resolve(result);
        t.onerror = () => reject(t.error);
        t.onabort = () => reject(t.error);
      }),
  );
}

export const putSegment = (s: LocalSegment) => tx("segments", "readwrite", (st) => st.put(s)).then(() => undefined);
export const deleteSegment = (s: Pick<LocalSegment, "userId" | "visitId" | "recordingNo" | "seq">) =>
  tx("segments", "readwrite", (st) => st.delete([s.userId, s.visitId, s.recordingNo, s.seq])).then(() => undefined);

export async function segmentsOf(userId: string, visitId: string, recordingNo: number): Promise<LocalSegment[]> {
  const list = (await tx<LocalSegment[]>("segments", "readonly", (st) => st.index("byRecording").getAll([userId, visitId, recordingNo]))) ?? [];
  return list.sort((a, b) => a.seq - b.seq);
}

export async function segmentKeysOf(userId: string): Promise<{ visitId: string; recordingNo: number; seq: number }[]> {
  const keys = (await tx<IDBValidKey[]>("segments", "readonly", (st) => st.index("byUser").getAllKeys(userId))) ?? [];
  return (keys as [string, string, number, number][]).map(([, visitId, recordingNo, seq]) => ({ visitId, recordingNo, seq }));
}

export const putRecording = (r: LocalRecording) => tx("recordings", "readwrite", (st) => st.put(r)).then(() => undefined);
export const deleteRecording = (r: Pick<LocalRecording, "userId" | "visitId" | "recordingNo">) =>
  tx("recordings", "readwrite", (st) => st.delete([r.userId, r.visitId, r.recordingNo])).then(() => undefined);
export const getRecording = (userId: string, visitId: string, recordingNo: number) =>
  tx<LocalRecording>("recordings", "readonly", (st) => st.get([userId, visitId, recordingNo]));
export async function recordingsOf(userId: string): Promise<LocalRecording[]> {
  return (await tx<LocalRecording[]>("recordings", "readonly", (st) => st.index("byUser").getAll(userId))) ?? [];
}
export async function allRecordings(): Promise<LocalRecording[]> {
  return (await tx<LocalRecording[]>("recordings", "readonly", (st) => st.getAll())) ?? [];
}

export async function sha256Hex(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Storage checks before recording (ТЗ §8.5): enough room for 2 hours, persistence, private mode. */
export async function storageCheck(): Promise<{ freeMb: number | null; persisted: boolean; privateLikely: boolean; enoughFor2h: boolean }> {
  let persisted = false;
  try {
    persisted = (await navigator.storage?.persisted?.()) ?? false;
    if (!persisted) persisted = (await navigator.storage?.persist?.()) ?? false;
  } catch {
    /* not supported */
  }
  let freeMb: number | null = null;
  let privateLikely = false;
  try {
    const est = await navigator.storage?.estimate?.();
    if (est?.quota !== undefined) {
      freeMb = Math.round(((est.quota ?? 0) - (est.usage ?? 0)) / 1_048_576);
      privateLikely = (est.quota ?? 0) < 150 * 1_048_576;
    }
  } catch {
    /* not supported */
  }
  // 2 hours at ~40 kbit/s ≈ 36 MB; keep a margin
  return { freeMb, persisted, privateLikely, enoughFor2h: freeMb === null || freeMb > 60 };
}
